/**
 * The social sign-in buttons on /login and /signup — Google, Apple, Microsoft,
 * LinkedIn — and what went wrong with them, in our words.
 *
 * Two ways a social sign-in fails: before the redirect (Clerk refuses to
 * start it) and after it (the person said no at the provider, or that account
 * belongs to someone else — Clerk brings them back here and leaves the reason
 * on the attempt, `useSocialRedirectError`). Both become one translated
 * message; a refusal the person caused is not reported.
 *
 * The provider sends the person back to the portal's `afterSignIn` /
 * `afterSignUp` — a satellite's redirect or a pending invitation survives the
 * round trip.
 *
 * The buttons are the providers the Clerk instance offers for signing in, in
 * the platform's order (`useEnabledSocialProviders`); none until Clerk loads.
 */
import { SOCIAL_PROVIDER_NAME, type SocialProvider } from '@tonnie/foundation';
import { useAuthErrorMessage, useTranslation } from '@tonnie/locale';
import { useAnalytics, useErrorCapture } from '@tonnie/observability';
import { useEnabledSocialProviders, useSocialRedirectError, useSocialSignIn } from '@tonnie/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAccountPortal } from '../portal';

export function useSocialSignInPanel(mode: 'sign-in' | 'sign-up') {
  const { t } = useTranslation();
  const describeAuthError = useAuthErrorMessage();
  const { captureException } = useErrorCapture();
  const { track } = useAnalytics();
  const { afterSignIn, afterSignUp } = useAccountPortal();
  const { ready: startReady, start } = useSocialSignIn(mode);
  const enabled = useEnabledSocialProviders('signIn');
  const cameBack = useSocialRedirectError();
  const [pending, setPending] = useState<SocialProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reported = useRef<unknown>(null);

  const failed = mode === 'sign-in' ? 'AUTH_LOGIN_FAILED' : 'AUTH_SIGNUP_FAILED';

  const explain = useCallback(
    (err: unknown, provider: SocialProvider | null) => {
      const failure = describeAuthError(
        err,
        provider
          ? t('auth.social.failed', { provider: SOCIAL_PROVIDER_NAME[provider] })
          : t('auth.social.failedGeneric')
      );
      if (provider) track(failed, { method: provider, error_code: failure.code });
      if (!failure.expected) {
        captureException(err instanceof Error ? err : new Error(String(err)), {
          component: 'social-sign-in',
          mode,
          provider: provider ?? 'unknown',
        });
      }
      setError(failure.message);
    },
    [describeAuthError, t, track, failed, captureException, mode]
  );

  // Back from the provider with a reason — say it once.
  useEffect(() => {
    if (!cameBack || reported.current === cameBack.error) return;
    reported.current = cameBack.error;
    explain(cameBack.error, cameBack.provider);
  }, [cameBack, explain]);

  const select = useCallback(
    async (provider: SocialProvider) => {
      setError(null);
      setPending(provider);
      track(mode === 'sign-in' ? 'AUTH_LOGIN_SUBMITTED' : 'AUTH_SIGNUP_SUBMITTED', {
        method: provider,
      });
      const result = await start(provider, mode === 'sign-in' ? afterSignIn() : afterSignUp());
      if (result.outcome === 'failed') {
        setPending(null);
        explain(result.error, provider);
      }
    },
    [start, track, mode, explain, afterSignIn, afterSignUp]
  );

  return {
    providers: enabled.providers,
    ready: startReady && enabled.ready,
    pending,
    error,
    select,
  };
}
